import { computed, ref } from 'vue'

import type { ComputedRef } from 'vue'
import type {
  DropZoneProps,
  DropZoneAcceptedFile,
  DropZoneRejectedItem,
  DropZoneItemPayload,
  DropZoneDisplayItem,
} from '../types/interfaces'

import { DROP_ZONE_STATUS, DROP_ZONE_ACCEPT, DROP_ZONE_MAX_SIZE, DROP_ZONE_MAX_FILES } from '../types/enums'
import { classifyFiles, buildFileId } from '../utils/classify-files'

interface UseDropZoneOptions {
  props: DropZoneProps
  onAccept: (files: DropZoneAcceptedFile[]) => void
  onReject: (items: DropZoneRejectedItem[]) => void
  onRemove: (payload: DropZoneItemPayload) => void
  onRetry: (payload: DropZoneItemPayload) => void
}

interface UseDropZoneReturn {
  displayItems: ComputedRef<DropZoneDisplayItem[]>
  rejectedItems: ComputedRef<DropZoneRejectedItem[]>
  hasAny: ComputedRef<boolean>
  isAtLimit: ComputedRef<boolean>
  addFiles: (files: File[]) => void
  removeItem: (id: string) => void
  retryItem: (id: string) => void
  clear: () => void
}

export const useDropZone = (options: UseDropZoneOptions): UseDropZoneReturn => {
  const { props, onAccept, onReject, onRemove, onRetry } = options

  const items = ref<DropZoneAcceptedFile[]>([])
  const rejected = ref<DropZoneRejectedItem[]>([])

  const acceptList = computed<string[]>(() => props.accept ?? DROP_ZONE_ACCEPT)
  const maxSize = computed<number>(() => props.maxSize ?? DROP_ZONE_MAX_SIZE)
  const maxFiles = computed<number>(() =>
    props.multiple === false ? 1 : (props.maxFiles ?? DROP_ZONE_MAX_FILES),
  )

  const isAtLimit = computed<boolean>(() => items.value.length >= maxFiles.value)

  const displayItems = computed<DropZoneDisplayItem[]>(() =>
    items.value.map(({ id, file }) => {
      const upload = props.uploads?.[id]
      return {
        id,
        name: file.name,
        size: file.size,
        status: upload?.status ?? DROP_ZONE_STATUS.Pending,
        progress: upload?.progress ?? 0,
        viewLink: upload?.viewLink ?? null,
        downloadLink: upload?.downloadLink ?? null,
        error: upload?.error ?? null,
      }
    }),
  )

  const rejectedItems = computed<DropZoneRejectedItem[]>(() => rejected.value)

  const hasAny = computed<boolean>(
    () => displayItems.value.length > 0 || rejected.value.length > 0,
  )

  const addFiles = (files: File[]): void => {
    if (props.disabled) return
    // Re-selecting a previously rejected file replaces its old rejection and gets
    // a fresh classification (it may pass now, e.g. after a slot freed up), while
    // remaining rejected ids stay in existingKeys to avoid duplicate v-for keys.
    const incomingIds = new Set(files.map(buildFileId))
    rejected.value = rejected.value.filter(item => !incomingIds.has(item.id))
    const existingKeys = new Set([
      ...items.value.map(i => i.id),
      ...rejected.value.map(i => i.id),
    ])
    const remainingSlots = Math.max(0, maxFiles.value - items.value.length)
    const result = classifyFiles(files, {
      accept: acceptList.value,
      maxSize: maxSize.value,
      remainingSlots,
      existingKeys,
    })
    if (result.accepted.length) {
      items.value = [...items.value, ...result.accepted]
      onAccept(result.accepted)
    }
    if (result.rejected.length) {
      rejected.value = [...rejected.value, ...result.rejected]
      onReject(result.rejected)
    }
  }

  const removeItem = (id: string): void => {
    const item = items.value.find(i => i.id === id)
    if (!item) return
    items.value = items.value.filter(i => i.id !== id)
    onRemove({ id, file: item.file })
  }

  const retryItem = (id: string): void => {
    const item = items.value.find(i => i.id === id)
    if (!item) return
    onRetry({ id, file: item.file })
  }

  const clear = (): void => {
    items.value = []
    rejected.value = []
  }

  return {
    displayItems,
    rejectedItems,
    hasAny,
    isAtLimit,
    addFiles,
    removeItem,
    retryItem,
    clear,
  }
}
